'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useCompetitionLabels } from '@/components/social/competition/competition-labels';
import { type CompetitionRankBarState, resolveCompetitionRankBarState } from '@/components/social/competition/competition-rank-bar-state';
import { type CompetitionViewerStanding, competitionRankingMetric, type PayoutWalletState, type TradingCompetitionSummary } from '@/components/social/competition/types';
import { rankCellNumeralClassName } from '@/components/social/shared/rank-numeral';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Badge } from '@/components/ui/app-badge';
import { Countdown } from '@/components/ui/countdown';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Alert01Icon, ChampionIconSolid, CheckmarkCircle02IconSolid, LinkForwardIconSolid, Loading03Icon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { competitionPath } from '@/lib/competitions/paths';
import { shortenWallet } from '@/lib/competitions/payout-wallet';
import { useFormattedCurrency } from '@/lib/currency';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { ShareModal, type ShareSurface } from '@/lib/share';
import { cn } from '@/lib/ui/utils';
export interface CompetitionRankBarProps {
  competition: TradingCompetitionSummary;
  initialNowMs: number | null;
  standing: CompetitionViewerStanding;
  wallet: PayoutWalletState;
  onAddWallet: () => void;
  /** Absent when the viewer's row is not in the loaded standings page. */
  onJumpToMe?: (() => void) | undefined;
}

// Same viewport-anchored placement as the Social rank bar (your-rank-bar.tsx):
// portaled to <body> so `fixed` + centre math resolve against the viewport.
const POSITIONING_CLASSES = 'fixed inset-x-3 bottom-[8px] z-overlay lg:bottom-4 lg:inset-x-auto lg:left-1/2 lg:max-w-[640px] lg:w-[min(640px,calc(100vw-2rem))] lg:-translate-x-1/2'; // ARBITRARY: 8px hugs the browser chrome to match the trade CTA; 640px desktop readability cap not expressible via spacing tokens.

function formatMetricValue(value: string | number, format: (d6: string) => string): string {
  return typeof value === 'number' ? value.toLocaleString() : format(value);
}
type Viewer = ReturnType<typeof useAuth>['user'];
export function CompetitionRankBar(props) {
  const $ = _c(12);
  const {
    isAuthenticated,
    isLoading,
    user
  } = useAuth();
  const isClient = useSyncExternalStore(subscribeToNothing, getClientSnapshot, getServerSnapshot);
  if (isLoading || !isClient) {
    return null;
  }
  let t0;
  if ($[0] !== isAuthenticated || $[1] !== props.competition.phase || $[2] !== props.standing || $[3] !== props.wallet) {
    t0 = resolveCompetitionRankBarState({
      isAuthenticated,
      phase: props.competition.phase,
      standing: props.standing,
      wallet: props.wallet
    });
    $[0] = isAuthenticated;
    $[1] = props.competition.phase;
    $[2] = props.standing;
    $[3] = props.wallet;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const state = t0;
  let t1;
  if ($[5] !== props || $[6] !== state || $[7] !== user) {
    t1 = <RankBarRow state={state} viewer={user} {...props} />;
    $[5] = props;
    $[6] = state;
    $[7] = user;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== state.kind || $[10] !== t1) {
    t2 = createPortal(<div className={POSITIONING_CLASSES} data-testid="competition-rank-bar" data-state={state.kind}>{t1}</div>, document.body);
    $[9] = state.kind;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
function subscribeToNothing(): () => void {
  return (): void => undefined;
}
function getClientSnapshot(): boolean {
  return true;
}
function getServerSnapshot(): boolean {
  return false;
}
function RankBarRow(t0) {
  const $ = _c(143);
  const {
    state,
    viewer,
    competition,
    initialNowMs,
    onAddWallet,
    onJumpToMe
  } = t0;
  const t = useTranslations("competitions.rankBar");
  const labels = useCompetitionLabels();
  const {
    openLogin
  } = useAuthGate();
  const {
    network
  } = useUserNetwork();
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const t1 = competition.prizePoolD6;
  let t2;
  if ($[0] !== competition || $[1] !== formatD6 || $[2] !== formatD6Signed || $[3] !== initialNowMs || $[4] !== labels || $[5] !== network || $[6] !== onAddWallet || $[7] !== onJumpToMe || $[8] !== openLogin || $[9] !== state.address || $[10] !== state.inPrizes || $[11] !== state.kind || $[12] !== state.message || $[13] !== state.rank || $[14] !== state.value || $[15] !== state.wallet || $[16] !== state.winningsD6 || $[17] !== t || $[18] !== viewer) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const prize = formatD6(t1, "compact");
      const rankingMetric = competitionRankingMetric(competition);
      const metricLabel = labels.metric(rankingMetric);
      const signed = rankingMetric === "pnl" || rankingMetric === "fairPnl" || rankingMetric === "cashProfit";
      let t3;
      if ($[20] !== formatD6 || $[21] !== formatD6Signed || $[22] !== signed) {
        t3 = value => formatMetricValue(value, d6 => signed ? formatD6Signed(d6, "compact") : formatD6(d6, "compact"));
        $[20] = formatD6;
        $[21] = formatD6Signed;
        $[22] = signed;
        $[23] = t3;
      } else {
        t3 = $[23];
      }
      const formatValue = t3;
      let t4;
      if ($[24] !== t) {
        t4 = (wallet, missing) => wallet === null ? missing : t("prizeWallet", {
          wallet: shortenWallet(wallet)
        });
        $[24] = t;
        $[25] = t4;
      } else {
        t4 = $[25];
      }
      const walletLine = t4;
      let t5;
      if ($[26] !== formatD6) {
        t5 = winningsD6 => {
          if (winningsD6 === null || !/^\d+$/.test(winningsD6) || BigInt(winningsD6) === BigInt(0)) {
            return null;
          }
          return formatD6(winningsD6, "compactTight");
        };
        $[26] = formatD6;
        $[27] = t5;
      } else {
        t5 = $[27];
      }
      const winningsLabel = t5;
      let t6;
      if ($[28] !== t || $[29] !== winningsLabel) {
        t6 = (winningsD6_0, final) => {
          const amount = winningsLabel(winningsD6_0);
          if (amount === null) {
            return;
          }
          return <Badge variant="secondary" className="shrink-0 text-micro" data-testid="competition-rank-bar-winnings">{final ? t("wonPrize", {
              amount
            }) : t("predictedWinnings", {
              amount
            })}</Badge>;
        };
        $[28] = t;
        $[29] = winningsLabel;
        $[30] = t6;
      } else {
        t6 = $[30];
      }
      const winningsTag = t6;
      let t7;
      if ($[31] !== competition.id || $[32] !== network) {
        t7 = userId => {
          if (typeof window === "undefined") {
            return "";
          }
          const url = new URL(competitionPath(competition.id), window.location.origin);
          withDemoMode(url, network === "mainnet" ? "mainnet" : "devnet");
          url.searchParams.set("rank", userId);
          return url.toString();
        };
        $[31] = competition.id;
        $[32] = network;
        $[33] = t7;
      } else {
        t7 = $[33];
      }
      const shareUrl = t7;
      let t8;
      if ($[34] !== competition.title || $[35] !== t) {
        t8 = (rank, winnings, final_0) => {
          const title = competition.title;
          if (final_0) {
            return winnings === null ? t("shareTextFinalOutside", {
              rank,
              title
            }) : t("shareTextFinal", {
              rank,
              title,
              winnings
            });
          }
          return winnings === null ? t("shareTextOutside", {
            rank,
            title
          }) : t("shareText", {
            rank,
            title,
            winnings
          });
        };
        $[34] = competition.title;
        $[35] = t;
        $[36] = t8;
      } else {
        t8 = $[36];
      }
      const shareText = t8;
      const shareAction = (rank_0, value_0, final_1, winningsD6_1) => {
        if (viewer === null) {
          return null;
        }
        const winnings_0 = winningsLabel(winningsD6_1);
        const surface = {
          kind: "competition_rank",
          competitionId: competition.id,
          userId: viewer.id,
          rank: rank_0,
          title: competition.title,
          valueLabel: formatValue(value_0),
          rankedByLabel: `${labels.board(competition.board)} · ${metricLabel}`,
          prizeLabel: prize,
          winningsLabel: winnings_0,
          phase: final_1 ? "concluded" : "live",
          handle: viewer.handle,
          displayName: viewer.displayName,
          avatarUrl: viewer.avatarNsfw === true ? null : viewer.avatarUrl
        };
        return <ShareModal surface={surface} url={shareUrl(viewer.id)} title={t("share")} text={shareText(rank_0, winnings_0, final_1)} trigger={<AnimatedButton variant="tertiary" size="sm" prefix={LinkForwardIconSolid} aria-label={t("share")} data-testid="competition-rank-bar-share" />} />;
      };
      let t9;
      if ($[37] !== onAddWallet || $[38] !== t) {
        t9 = wallet_0 => <AnimatedButton variant={wallet_0 === null ? "primary" : "secondary"} size="sm" onClick={onAddWallet} data-testid="competition-rank-bar-wallet">{wallet_0 === null ? t("addWallet") : t("edit")}</AnimatedButton>;
        $[37] = onAddWallet;
        $[38] = t;
        $[39] = t9;
      } else {
        t9 = $[39];
      }
      const walletAction = t9;
      switch (state.kind) {
        case "signed-out":
          {
            const t10 = t("signedOutTitle", {
              prize
            });
            let t11;
            if ($[40] !== t) {
              t11 = t("signedOutDescription");
              $[40] = t;
              $[41] = t11;
            } else {
              t11 = $[41];
            }
            let t12;
            if ($[42] !== t) {
              t12 = t("signIn");
              $[42] = t;
              $[43] = t12;
            } else {
              t12 = $[43];
            }
            let t13;
            if ($[44] !== openLogin || $[45] !== t12) {
              t13 = <AnimatedButton variant="primary" size="sm" onClick={openLogin}>{t12}</AnimatedButton>;
              $[44] = openLogin;
              $[45] = t12;
              $[46] = t13;
            } else {
              t13 = $[46];
            }
            t2 = <Row icon={ChampionIconSolid} title={t10} description={t11} action={t13} />;
            break bb0;
          }
        case "saving":
          {
            let t10;
            if ($[47] !== t) {
              t10 = t("savingTitle");
              $[47] = t;
              $[48] = t10;
            } else {
              t10 = $[48];
            }
            let t11;
            if ($[49] !== state.address) {
              t11 = shortenWallet(state.address);
              $[49] = state.address;
              $[50] = t11;
            } else {
              t11 = $[50];
            }
            let t12;
            if ($[51] !== t10 || $[52] !== t11) {
              t12 = <Row icon={Loading03Icon} iconClassName="animate-spin text-foreground-tertiary" title={t10} description={t11} />;
              $[51] = t10;
              $[52] = t11;
              $[53] = t12;
            } else {
              t12 = $[53];
            }
            t2 = t12;
            break bb0;
          }
        case "error":
          {
            let t10;
            if ($[54] !== t) {
              t10 = t("errorTitle");
              $[54] = t;
              $[55] = t10;
            } else {
              t10 = $[55];
            }
            let t11;
            if ($[56] !== state.message || $[57] !== t) {
              t11 = state.message ?? t("errorDescription");
              $[56] = state.message;
              $[57] = t;
              $[58] = t11;
            } else {
              t11 = $[58];
            }
            let t12;
            if ($[59] !== t) {
              t12 = t("retry");
              $[59] = t;
              $[60] = t12;
            } else {
              t12 = $[60];
            }
            let t13;
            if ($[61] !== onAddWallet || $[62] !== t12) {
              t13 = <AnimatedButton variant="secondary" size="sm" onClick={onAddWallet}>{t12}</AnimatedButton>;
              $[61] = onAddWallet;
              $[62] = t12;
              $[63] = t13;
            } else {
              t13 = $[63];
            }
            let t14;
            if ($[64] !== t10 || $[65] !== t11 || $[66] !== t13) {
              t14 = <Row icon={Alert01Icon} iconClassName="text-feedback-error-content" title={t10} description={t11} action={t13} />;
              $[64] = t10;
              $[65] = t11;
              $[66] = t13;
              $[67] = t14;
            } else {
              t14 = $[67];
            }
            t2 = t14;
            break bb0;
          }
        case "entered":
          {
            const t10 = state.wallet === null ? ChampionIconSolid : CheckmarkCircle02IconSolid;
            const t11 = state.wallet === null ? undefined : "text-feedback-success-content";
            let t12;
            if ($[68] !== t) {
              t12 = t("enteredStartsIn");
              $[68] = t;
              $[69] = t12;
            } else {
              t12 = $[69];
            }
            let t13;
            if ($[70] !== competition.start) {
              t13 = Date.parse(competition.start);
              $[70] = competition.start;
              $[71] = t13;
            } else {
              t13 = $[71];
            }
            let t14;
            if ($[72] !== t) {
              t14 = t("started");
              $[72] = t;
              $[73] = t14;
            } else {
              t14 = $[73];
            }
            let t15;
            if ($[74] !== t) {
              t15 = countdown => t("startCountdownLabel", {
                countdown
              });
              $[74] = t;
              $[75] = t15;
            } else {
              t15 = $[75];
            }
            let t16;
            if ($[76] !== initialNowMs || $[77] !== t13 || $[78] !== t14 || $[79] !== t15) {
              t16 = <Countdown targetMs={t13} initialNowMs={initialNowMs} endedLabel={t14} ariaLabel={t15} className="tabular-nums" />;
              $[76] = initialNowMs;
              $[77] = t13;
              $[78] = t14;
              $[79] = t15;
              $[80] = t16;
            } else {
              t16 = $[80];
            }
            let t17;
            if ($[81] !== t12 || $[82] !== t16) {
              t17 = <>{t12}{" "}{t16}</>;
              $[81] = t12;
              $[82] = t16;
              $[83] = t17;
            } else {
              t17 = $[83];
            }
            let t18;
            if ($[84] !== state.wallet || $[85] !== t || $[86] !== walletLine) {
              t18 = walletLine(state.wallet, t("addWalletHint"));
              $[84] = state.wallet;
              $[85] = t;
              $[86] = walletLine;
              $[87] = t18;
            } else {
              t18 = $[87];
            }
            let t19;
            if ($[88] !== state.wallet || $[89] !== walletAction) {
              t19 = walletAction(state.wallet);
              $[88] = state.wallet;
              $[89] = walletAction;
              $[90] = t19;
            } else {
              t19 = $[90];
            }
            let t20;
            if ($[91] !== t10 || $[92] !== t11 || $[93] !== t17 || $[94] !== t18 || $[95] !== t19) {
              t20 = <Row icon={t10} iconClassName={t11} title={t17} description={t18} action={t19} />;
              $[91] = t10;
              $[92] = t11;
              $[93] = t17;
              $[94] = t18;
              $[95] = t19;
              $[96] = t20;
            } else {
              t20 = $[96];
            }
            t2 = t20;
            break bb0;
          }
        case "unranked":
          {
            let t10;
            if ($[97] !== t) {
              t10 = t("noActivityTitle");
              $[97] = t;
              $[98] = t10;
            } else {
              t10 = $[98];
            }
            let t11;
            if ($[99] !== state.wallet || $[100] !== t || $[101] !== walletLine) {
              t11 = walletLine(state.wallet, t("addWalletHint"));
              $[99] = state.wallet;
              $[100] = t;
              $[101] = walletLine;
              $[102] = t11;
            } else {
              t11 = $[102];
            }
            let t12;
            if ($[103] !== state.wallet || $[104] !== walletAction) {
              t12 = walletAction(state.wallet);
              $[103] = state.wallet;
              $[104] = walletAction;
              $[105] = t12;
            } else {
              t12 = $[105];
            }
            let t13;
            if ($[106] !== t10 || $[107] !== t11 || $[108] !== t12) {
              t13 = <Row icon={ChampionIconSolid} title={t10} description={t11} action={t12} />;
              $[106] = t10;
              $[107] = t11;
              $[108] = t12;
              $[109] = t13;
            } else {
              t13 = $[109];
            }
            t2 = t13;
            break bb0;
          }
        case "final":
          {
            const t10 = state.rank;
            let t11;
            if ($[110] !== formatValue || $[111] !== state.value || $[112] !== t) {
              t11 = t("finalStanding", {
                value: formatValue(state.value)
              });
              $[110] = formatValue;
              $[111] = state.value;
              $[112] = t;
              $[113] = t11;
            } else {
              t11 = $[113];
            }
            let t12;
            if ($[114] !== state.winningsD6 || $[115] !== winningsTag) {
              t12 = winningsTag(state.winningsD6, true);
              $[114] = state.winningsD6;
              $[115] = winningsTag;
              $[116] = t12;
            } else {
              t12 = $[116];
            }
            let t13;
            if ($[117] !== state.inPrizes || $[118] !== state.wallet || $[119] !== t) {
              t13 = state.inPrizes ? state.wallet === null ? t("inPrizesNoWallet") : t("prizePaid", {
                wallet: shortenWallet(state.wallet)
              }) : t("outsidePrizes");
              $[117] = state.inPrizes;
              $[118] = state.wallet;
              $[119] = t;
              $[120] = t13;
            } else {
              t13 = $[120];
            }
            let t14;
            if ($[121] !== onJumpToMe || $[122] !== state.inPrizes || $[123] !== state.wallet || $[124] !== t || $[125] !== walletAction) {
              t14 = state.inPrizes && state.wallet === null ? walletAction(null) : onJumpToMe === undefined ? null : <AnimatedButton variant="secondary" size="sm" onClick={onJumpToMe}>{t("jumpToMe")}</AnimatedButton>;
              $[121] = onJumpToMe;
              $[122] = state.inPrizes;
              $[123] = state.wallet;
              $[124] = t;
              $[125] = walletAction;
              $[126] = t14;
            } else {
              t14 = $[126];
            }
            t2 = <Row rank={t10} title={t11} tag={t12} share={shareAction(state.rank, state.value, true, state.winningsD6)} description={t13} action={t14} />;
            break bb0;
          }
        case "ranked":
          {
            let t10;
            if ($[127] !== state.winningsD6 || $[128] !== winningsLabel) {
              t10 = winningsLabel(state.winningsD6);
              $[127] = state.winningsD6;
              $[128] = winningsLabel;
              $[129] = t10;
            } else {
              t10 = $[129];
            }
            const estimatedWinnings = t10;
            const hasEstimatedWinnings = estimatedWinnings !== null;
            const title_0 = rankingMetric === "pnl" ? t("yourPnlStanding", {
              value: formatValue(state.value)
            }) : t("yourStanding", {
              metric: metricLabel,
              value: formatValue(state.value)
            });
            let t11;
            if ($[130] !== hasEstimatedWinnings || $[131] !== state.wallet || $[132] !== t) {
              t11 = hasEstimatedWinnings ? state.wallet === null ? t("addWalletForEstimatedPrize") : <span className="inline-flex items-center gap-1"><Icon icon={CheckmarkCircle02IconSolid} aria-hidden={true} className="size-icon-xs text-feedback-success-content" />{t("prizeWallet", {
                  wallet: shortenWallet(state.wallet)
                })}</span> : t("outsideLivePrizes");
              $[130] = hasEstimatedWinnings;
              $[131] = state.wallet;
              $[132] = t;
              $[133] = t11;
            } else {
              t11 = $[133];
            }
            const description = t11;
            let t12;
            if ($[134] !== hasEstimatedWinnings || $[135] !== onJumpToMe || $[136] !== state.wallet || $[137] !== t || $[138] !== walletAction) {
              t12 = hasEstimatedWinnings && state.wallet === null ? walletAction(null) : onJumpToMe === undefined ? null : <AnimatedButton variant="secondary" size="sm" onClick={onJumpToMe}>{t("jumpToMe")}</AnimatedButton>;
              $[134] = hasEstimatedWinnings;
              $[135] = onJumpToMe;
              $[136] = state.wallet;
              $[137] = t;
              $[138] = walletAction;
              $[139] = t12;
            } else {
              t12 = $[139];
            }
            const action = t12;
            const t13 = state.rank;
            let t14;
            if ($[140] !== state.winningsD6 || $[141] !== winningsTag) {
              t14 = winningsTag(state.winningsD6, false);
              $[140] = state.winningsD6;
              $[141] = winningsTag;
              $[142] = t14;
            } else {
              t14 = $[142];
            }
            t2 = <Row rank={t13} title={title_0} tag={t14} share={shareAction(state.rank, state.value, false, state.winningsD6)} description={description} action={action} />;
            break bb0;
          }
      }
    }
    $[0] = competition;
    $[1] = formatD6;
    $[2] = formatD6Signed;
    $[3] = initialNowMs;
    $[4] = labels;
    $[5] = network;
    $[6] = onAddWallet;
    $[7] = onJumpToMe;
    $[8] = openLogin;
    $[9] = state.address;
    $[10] = state.inPrizes;
    $[11] = state.kind;
    $[12] = state.message;
    $[13] = state.rank;
    $[14] = state.value;
    $[15] = state.wallet;
    $[16] = state.winningsD6;
    $[17] = t;
    $[18] = viewer;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
}
function Row(t0) {
  const $ = _c(21);
  const {
    icon,
    iconClassName,
    rank,
    title,
    tag,
    description,
    share,
    action
  } = t0;
  let t1;
  if ($[0] !== icon || $[1] !== iconClassName || $[2] !== rank) {
    t1 = rank !== undefined ? <span className={cn(rankCellNumeralClassName, "shrink-0 text-heading-md")}>#{rank}</span> : icon !== undefined ? <Icon icon={icon} aria-hidden={true} className={cn("size-icon-lg shrink-0 text-product-market-champion", iconClassName)} /> : null;
    $[0] = icon;
    $[1] = iconClassName;
    $[2] = rank;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== title) {
    t2 = <span className="min-w-0 truncate">{title}</span>;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2 || $[7] !== tag) {
    t3 = <p className="flex min-w-0 items-center gap-1.5 text-body font-medium text-foreground-primary">{t2}{tag}</p>;
    $[6] = t2;
    $[7] = tag;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== description) {
    t4 = <p className="truncate text-caption text-foreground-tertiary">{description}</p>;
    $[9] = description;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="min-w-0 sm:flex-1">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== action || $[15] !== share) {
    t6 = share !== undefined || action !== undefined ? <div className="col-span-2 flex items-center justify-end gap-1.5 sm:col-span-1 sm:shrink-0">{share}{action}</div> : null;
    $[14] = action;
    $[15] = share;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t1 || $[18] !== t5 || $[19] !== t6) {
    t7 = <Panel elevation="elevated" density="compact"><PanelContent className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 sm:flex sm:gap-3">{t1}{t5}{t6}</PanelContent></Panel>;
    $[17] = t1;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}